import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CountUp from 'react-countup'
import Marquee from 'react-fast-marquee'
import { AppUseSelector } from '../../../redux/hooks'
import { getRecommended, getScore, notLearnedLanguage } from '../../../redux/actions/onboardingAction'
import { trackOnboarding } from '../../../utils/analytics'
import { hapticSuccess } from '../../../utils/haptics'
import { formatNumber } from '../../../i18n/format'
import { BookIcon, LayersIcon, TapIcon } from './icons'
import { useLanguageName } from '../stepContext'
import { localizedLevelLabel } from '../../story-card/constants'

const randomId = () => Math.random().toString(36).slice(2, 12) + Date.now().toString(36)

// Translation keys, by learning goal.
const GOAL_LINES: Record<string, string> = {
  travel: 'onboarding:result.goalLines.travel',
  work: 'onboarding:result.goalLines.work',
  family: 'onboarding:result.goalLines.family',
  exams: 'onboarding:result.goalLines.exams',
  culture: 'onboarding:result.goalLines.culture',
  challenge: 'onboarding:result.goalLines.challenge',
}

// The word count rolls up in the interface language's digit grouping (en: 1,234).
const formatWordCount = (value: number) => formatNumber(value, { maximumFractionDigits: 0 })

const CONFETTI_COLORS = ['#068466', '#0aa37e', '#f6a623', '#e2f3ee', '#f9d24f']

// CSS custom properties each confetti piece reads in onboarding.css.
const CONFETTI_VARS: Record<'x' | 'y' | 'rotate', any> = { x: '--cx', y: '--cy', rotate: '--cr' }

const Confetti = () => (
  <span className='ob-confetti' aria-hidden='true'>
    {Array.from({ length: 14 }).map((_, i) => {
      const angle = (i / 14) * Math.PI * 2
      const distance = 95 + (i % 3) * 26
      const style = {
        background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        [CONFETTI_VARS.x]: `${Math.cos(angle) * distance}px`,
        [CONFETTI_VARS.y]: `${Math.sin(angle) * distance}px`,
        [CONFETTI_VARS.rotate]: `${(i % 2 ? 1 : -1) * (180 + i * 24)}deg`,
        animationDelay: `${0.45 + (i % 5) * 0.05}s`,
      } as React.CSSProperties
      return <i key={i} style={style} />
    })}
  </span>
)

const ResultStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const { experience, goal, tempUser, languageInfo, wordResult, placementDone } = AppUseSelector(
    ({ onboarding }) => onboarding,
  )
  const [computing, setComputing] = useState(true)
  const [finishing, setFinishing] = useState(false)
  const recommendationRef = useRef<Promise<any> | null>(null)
  const claimedRef = useRef(false)

  const tookTest = experience === 'yes'
  const languageName = useLanguageName(languageInfo?.language)
  // The server sends the converged level as a code and its English name; show it translated.
  const levelDisplay = tookTest
    ? localizedLevelLabel(placementDone?.convergedLevel, placementDone?.convergedLevelDisplay) || t('result.yourLevel')
    : t('result.beginnerLevel')
  const activePreferenceId = languageInfo?.preferences?.find((p: any) => p.is_active)?._id

  useEffect(() => {
    if (claimedRef.current) return
    claimedRef.current = true

    const claim = async () => {
      if (tookTest) {
        await getScore({ temp_user: tempUser, learning_goal: goal } as any)
      } else {
        await notLearnedLanguage({
          temp_user: tempUser || `fresh-${randomId()}`,
          learning_goal: goal,
          language_id: languageInfo?._id,
          language_preference_id: activePreferenceId,
        } as any)
      }
      setComputing(false)
      // Pre-warm the first story recommendation while the user enjoys the
      // result — by the time they hit Continue it's usually done.
      recommendationRef.current = getRecommended({ languageId: languageInfo?._id })
    }
    claim()
  }, [])

  useEffect(() => {
    if (!computing) {
      trackOnboarding('result_viewed', {
        took_test: tookTest,
        score: wordResult?.score,
        level: placementDone?.convergedLevel || 'BEGINNER_1',
      })
      // Celebrate the reveal in time with the confetti / count-up animation.
      hapticSuccess()
    }
  }, [computing])

  const handleContinue = async () => {
    setFinishing(true)
    try {
      await recommendationRef.current
    } catch {
      /* recommendation failure shouldn't block onboarding */
    }
    next()
  }

  if (computing) {
    return (
      <div className='ob-step'>
        <div className='ob-loader'>
          <div className='ob-spinner' />
          <p className='ob-loader-title'>{tookTest ? t('result.computingTest') : t('result.computingFresh')}</p>
          <p className='ob-loader-sub'>{t('result.computingSub')}</p>
        </div>
      </div>
    )
  }

  if (finishing) {
    return (
      <div className='ob-step'>
        <div className='ob-loader'>
          <div className='ob-spinner' />
          <p className='ob-loader-title'>{t('result.finishingTitle')}</p>
          <p className='ob-loader-sub'>{t('result.finishingSub')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='ob-step'>
      <div className='ob-result-hero'>
        <div className='ob-result-badge-wrap'>
          <svg className='ob-result-ring' viewBox='0 0 148 148' aria-hidden='true'>
            <circle className='ring-track' cx='74' cy='74' r='66' />
            <circle className='ring-fill' cx='74' cy='74' r='66' />
          </svg>
          <div className='ob-result-badge'>
            <span className='ob-result-badge-label'>
              {tookTest ? t('result.badgeTest') : t('result.badgeFresh')}
            </span>
            <span className='ob-result-badge-level'>
              {levelDisplay}
              {tookTest ? ' 🎉' : ''}
            </span>
          </div>
          <Confetti />
        </div>

        {tookTest && wordResult?.score ? (
          <>
            <div className='ob-result-score'>
              <CountUp duration={1.2} end={Math.round(wordResult.score)} formattingFn={formatWordCount} />
            </div>
            <p className='ob-result-score-sub'>{t('result.wordsKnown')}</p>
          </>
        ) : (
          <>
            <div className='ob-result-score'>{t('result.dayOne', { language: languageName })}</div>
            <p className='ob-result-score-sub'>{t('result.freshSub')}</p>
          </>
        )}

        {tookTest && <p className='ob-result-dict-note'>{t('result.dictNote')}</p>}

        <div className='ob-method'>
          <h2 className='ob-method-title'>{t('result.methodTitle')}</h2>
          <p className='ob-method-personal'>{t(GOAL_LINES[goal] || 'onboarding:result.goalLines.default')}</p>
          <ul className='ob-method-steps'>
            <li className='ob-method-step'>
              <span className='ob-method-step-icon' aria-hidden='true'>
                <BookIcon />
              </span>
              <span>
                <p className='ob-method-step-title'>{t('result.steps.lesson.title', { level: levelDisplay })}</p>
                <p className='ob-method-step-text'>{t('result.steps.lesson.text')}</p>
              </span>
            </li>
            <li className='ob-method-step'>
              <span className='ob-method-step-icon' aria-hidden='true'>
                <TapIcon />
              </span>
              <span>
                <p className='ob-method-step-title'>{t('result.steps.tap.title')}</p>
                <p className='ob-method-step-text'>{t('result.steps.tap.text')}</p>
              </span>
            </li>
            <li className='ob-method-step'>
              <span className='ob-method-step-icon' aria-hidden='true'>
                <LayersIcon />
              </span>
              <span>
                <p className='ob-method-step-title'>{t('result.steps.mastery.title')}</p>
                <p className='ob-method-step-text'>{t('result.steps.mastery.text')}</p>
              </span>
            </li>
          </ul>
        </div>

        <div className='ob-actions' style={{ justifyContent: 'center' }}>
          <button type='button' className='ob-cta ob-cta--block' onClick={handleContinue}>
            {t('result.cta')}
          </button>
        </div>
      </div>

      {tookTest && wordResult?.knownWords?.words?.length > 0 && (
        <div className='ob-marquee-wrap'>
          <Marquee autoFill speed={32}>
            {wordResult.knownWords.words.slice(0, 60).map((word: string, i: number) => (
              <p className='text' key={`${word}-${i}`} lang='zh'>
                {word}
              </p>
            ))}
          </Marquee>
        </div>
      )}
    </div>
  )
}

export default ResultStep
